/**
 * Walk-in / regular customers — browse, search, and CORRECT.
 *
 * WHY THIS EXISTS
 * ---------------
 * Until now nothing in the app could change a customer. A name captured wrongly — the phone
 * bot writing down "please tell double e." — was permanent, reachable from search, invoices
 * and service history forever, and fixable nowhere. This is that repair path, for the
 * customer and for their vehicles.
 *
 * ⚠️ A Customer is SHARED. Invoices join the live record and render the name at read time,
 * so a rename changes what past invoices display. That is exactly what you want when fixing
 * a typo, and not what you want when a vehicle changes hands — hence the warning on the
 * form rather than a silent save.
 *
 * Vehicles are edited in place for the same reason: the bot created rows with the literal
 * make "Unknown", and the booking flow is the wrong place to go hunting for them.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
    Search, Loader2, AlertCircle, ChevronDown, ChevronRight,
    ChevronLeft, Car, Pencil, Check, Calendar, Trash2, Trash, FlaskConical, Undo2,
} from 'lucide-react';
import { listCustomersApi, updateCustomerApi, deleteCustomerApi } from '../services/customerApi';
import { updateVehicleApi, deleteVehicleApi } from '../services/bookingApi';
import { hasPermission } from '../services/authService';
import { rangeFromMonth, currentMonthValue, shiftMonth } from '../utils/monthRange';
import DeleteCustomerDialog from './DeleteCustomerDialog';
import { stashContact, findStashedContact, removeStashedContact } from '../utils/contactNote';

const PAGE_SIZE = 20;

/** Matches the backend: "Unknown" is the old placeholder for "not supplied". */
const meaningful = (v) => !!v && v.trim() !== '' && v.trim().toLowerCase() !== 'unknown';

const vehicleLabel = (v) => {
    const parts = [];
    if (v.year && Number(v.year) > 0) parts.push(v.year);
    if (meaningful(v.make)) parts.push(v.make);
    if (meaningful(v.model)) parts.push(v.model);
    const described = parts.join(' ').trim();
    return described || v.regoNumber || 'Vehicle';
};

const Field = ({ label, value, onChange, placeholder, type = 'text', required }) => (
    <div>
        <label className="mb-1 block text-xs font-medium text-gray-600">
            {label}{required && <span className="text-red-500"> *</span>}
        </label>
        <input
            type={type}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500"
        />
    </div>
);

// ── Vehicle row ───────────────────────────────────────────────────────────────

const VehicleRow = ({ vehicle, onSaved, canDelete }) => {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [draft, setDraft] = useState({});
    /*
     * Confirm inline rather than via window.confirm: a native dialog blocks the whole
     * page, reads as a browser popup rather than part of the app, and gives no room to
     * say what is about to be destroyed. This is a delete that also takes service
     * history, so the warning has to be specific.
     */
    const [confirming, setConfirming] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const start = () => {
        // Placeholders are cleared rather than pre-filled: leaving "Unknown" in the box
        // invites someone to save it straight back.
        setDraft({
            rego: vehicle.regoNumber ?? '',
            make: meaningful(vehicle.make) ? vehicle.make : '',
            model: meaningful(vehicle.model) ? vehicle.model : '',
            year: vehicle.year && Number(vehicle.year) > 0 ? String(vehicle.year) : '',
        });
        setError(null);
        setEditing(true);
    };

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            await updateVehicleApi(vehicle.uuid, {
                rego: draft.rego?.trim() || null,
                make: draft.make?.trim() || null,
                model: draft.model?.trim() || null,
                year: draft.year ? Number(draft.year) : null,
            });
            setEditing(false);
            onSaved?.();
        } catch (e) {
            setError(e?.response?.data?.message || 'Could not save this vehicle.');
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        setDeleting(true);
        setError(null);
        try {
            await deleteVehicleApi(vehicle.uuid);
            // No local removal — the parent reloads the page it is on, so the row
            // disappears with a count that still matches the server.
            onSaved?.();
        } catch (e) {
            /*
             * The server's message is the useful one: a blocked delete names how many
             * bookings and jobs are holding the vehicle, which is what tells staff where
             * to go next. A generic string would throw that away.
             */
            setError(e?.response?.data?.message || 'Could not delete this vehicle.');
            setConfirming(false);
        } finally {
            setDeleting(false);
        }
    };

    if (!editing) {
        const needsDetail = !meaningful(vehicle.make) || !meaningful(vehicle.model);
        return (
            <li className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <Car size={13} className="shrink-0 text-gray-400" />
                            <span className="truncate text-sm text-gray-900">{vehicleLabel(vehicle)}</span>
                        </div>
                        <div className="mt-0.5 font-mono text-xs text-gray-500">{vehicle.regoNumber}</div>
                        {needsDetail && (
                            <div className="mt-0.5 text-[11px] text-amber-700">
                                Make/model missing — recorded before these were required
                            </div>
                        )}
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                        <button
                            type="button"
                            onClick={start}
                            className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
                        >
                            <Pencil size={12} className="mr-1 inline" />Edit
                        </button>
                        {/* Presentation only — the server enforces vehicle:delete regardless.
                            Hidden rather than disabled: a permanently greyed-out Delete on
                            every vehicle is noise for staff who will never be granted it. */}
                        {canDelete && !confirming && (
                            <button
                                type="button"
                                onClick={() => { setError(null); setConfirming(true); }}
                                aria-label={`Delete ${vehicle.regoNumber}`}
                                className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                            >
                                <Trash2 size={12} className="mr-1 inline" />Delete
                            </button>
                        )}
                    </div>
                </div>

                {confirming && (
                    <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                        {/* Names the rego and the second consequence. Staff reading "Delete"
                            expect the vehicle to go; they do not expect the service history
                            to go with it, so that is stated before they commit, not after. */}
                        <p className="text-xs text-red-800">
                            Delete <span className="font-mono font-semibold">{vehicle.regoNumber}</span> and
                            its service history? This can't be undone.
                        </p>
                        <div className="mt-2 flex justify-end gap-2">
                            <button type="button" onClick={() => setConfirming(false)} disabled={deleting}
                                    className="rounded-lg px-3 py-1.5 text-xs text-gray-600 hover:bg-white disabled:opacity-50">
                                Cancel
                            </button>
                            <button type="button" onClick={remove} disabled={deleting}
                                    className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50">
                                {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                                Delete vehicle
                            </button>
                        </div>
                    </div>
                )}

                {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
            </li>
        );
    }

    return (
        <li className="rounded-lg border border-violet-300 bg-violet-50/40 px-3 py-2.5">
            <div className="grid grid-cols-2 gap-2">
                <Field label="Rego" value={draft.rego} onChange={(v) => setDraft(d => ({ ...d, rego: v }))} placeholder="ABC123" />
                <Field label="Year" value={draft.year} onChange={(v) => setDraft(d => ({ ...d, year: v }))} placeholder="2020" type="number" />
                <Field label="Make" value={draft.make} onChange={(v) => setDraft(d => ({ ...d, make: v }))} placeholder="Toyota" />
                <Field label="Model" value={draft.model} onChange={(v) => setDraft(d => ({ ...d, model: v }))} placeholder="Camry" />
            </div>
            {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
            <div className="mt-2 flex justify-end gap-2">
                <button type="button" onClick={() => setEditing(false)}
                        className="rounded-lg px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-100">
                    Cancel
                </button>
                <button type="button" onClick={save} disabled={saving}
                        className="inline-flex items-center gap-1 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-50">
                    {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                    Save vehicle
                </button>
            </div>
        </li>
    );
};

// ── Customer row ──────────────────────────────────────────────────────────────

const CustomerRow = ({ customer, onSaved, canDeleteVehicle, canDelete, onDelete }) => {
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [draft, setDraft] = useState({});

    const isBusiness = customer.customerType === 'BUSINESS';

    const startEdit = () => {
        setDraft({
            fullName: isBusiness ? '' : (customer.fullName ?? ''),
            businessName: isBusiness ? (customer.businessName ?? '') : '',
            phone: customer.phone ?? '',
            email: customer.email ?? '',
            address: customer.address ?? '',
            suburb: customer.suburb ?? '',
            state: customer.state ?? '',
            postcode: customer.postcode ?? '',
            notes: customer.notes ?? '',
        });
        setError(null);
        setEditing(true);
        setOpen(true);
    };

    /**
     * Keep the real contact in a note, so the workshop can type its own phone and
     * email over the top to test the customer emails and texts, and put the real ones
     * back afterwards — see utils/contactNote.
     *
     * <p>Only the DRAFT changes here; nothing reaches the server until Save. The note
     * is written from what the fields hold NOW, which is the real contact the first
     * time and is refused the second time, so a double press cannot lose it.
     */
    const keepRealContact = () => setDraft((d) => ({
        ...d,
        notes: stashContact({ phone: d.phone, email: d.email, notes: d.notes }),
    }));

    /** The real contact back into the fields, and its line out of the notes. */
    const restoreContact = () => setDraft((d) => {
        const stashed = findStashedContact(d.notes);
        if (!stashed) return d;
        return { ...d, phone: stashed.phone, email: stashed.email, notes: removeStashedContact(d.notes) };
    });

    const stashed = editing ? findStashedContact(draft.notes) : findStashedContact(customer.notes);

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            await updateCustomerApi(customer.uuid, draft);
            setEditing(false);
            onSaved?.();
        } catch (e) {
            setError(e?.response?.data?.message || 'Could not save this customer.');
        } finally {
            setSaving(false);
        }
    };

    const vehicles = customer.vehicles ?? [];

    return (
        <li className="rounded-xl border border-gray-200 bg-white">
            <div className="flex items-center gap-2 px-4 py-3">
                <button type="button" onClick={() => setOpen(o => !o)}
                        className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-gray-100">
                    {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                <button type="button" onClick={() => setOpen(o => !o)} className="min-w-0 flex-1 text-left">
                    <p className="truncate text-sm font-semibold text-gray-900">
                        {customer.fullName || customer.businessName || 'Unnamed customer'}
                    </p>
                    <p className="truncate text-xs text-gray-500">
                        {customer.phone || 'No phone'}
                        {customer.email ? ` · ${customer.email}` : ' · No email'}
                        {vehicles.length > 0 && ` · ${vehicles.length} vehicle${vehicles.length === 1 ? '' : 's'}`}
                    </p>
                </button>
                <button type="button" onClick={startEdit}
                        className="shrink-0 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50">
                    <Pencil size={12} className="mr-1 inline" />Edit
                </button>
                {/* Presentation only — the server enforces customer:delete regardless.
                    Hidden rather than disabled: a permanently greyed-out Delete on every
                    row is noise for staff who will never be granted it. */}
                {canDelete && (
                    <button type="button" onClick={() => onDelete(customer)}
                            aria-label={`Delete ${customer.fullName || customer.businessName || 'customer'}`}
                            className="shrink-0 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600">
                        <Trash size={12} className="mr-1 inline" />Delete
                    </button>
                )}
            </div>

            {open && (
                <div className="border-t border-gray-100 px-4 py-3">
                    {editing ? (
                        <>
                            <div className="grid grid-cols-2 gap-3">
                                {isBusiness ? (
                                    <Field label="Business name" required value={draft.businessName}
                                           onChange={(v) => setDraft(d => ({ ...d, businessName: v }))} />
                                ) : (
                                    <Field label="Full name" required value={draft.fullName}
                                           onChange={(v) => setDraft(d => ({ ...d, fullName: v }))} />
                                )}
                                <Field label="Phone" required value={draft.phone}
                                       onChange={(v) => setDraft(d => ({ ...d, phone: v }))} />
                                <Field label="Email" type="email" value={draft.email}
                                       onChange={(v) => setDraft(d => ({ ...d, email: v }))} />
                                <Field label="Address" value={draft.address}
                                       onChange={(v) => setDraft(d => ({ ...d, address: v }))} />
                                <Field label="Suburb" value={draft.suburb}
                                       onChange={(v) => setDraft(d => ({ ...d, suburb: v }))} />
                                <Field label="State" value={draft.state}
                                       onChange={(v) => setDraft(d => ({ ...d, state: v }))} />
                                <Field label="Postcode" value={draft.postcode}
                                       onChange={(v) => setDraft(d => ({ ...d, postcode: v }))} />
                            </div>

                            {/* Testing the customer emails and texts: keep the real contact in
                                the note below, type your own over the top, and put the real
                                one back when you are done. */}
                            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                                {stashed ? (
                                    <button type="button" onClick={restoreContact}
                                            className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100">
                                        <Undo2 size={12} /> Put the real contact back
                                    </button>
                                ) : (
                                    <button type="button" onClick={keepRealContact}
                                            className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-white px-2.5 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100">
                                        <FlaskConical size={12} /> Keep the real contact in a note
                                    </button>
                                )}
                                <span className="text-[11px] text-amber-800">
                                    {stashed
                                        ? `Kept since ${stashed.stashedOn}: ${stashed.phone || 'no phone'} · ${stashed.email || 'no email'}`
                                        : 'Then type your own phone and email above to test the customer emails and texts.'}
                                </span>
                            </div>

                            <div className="mt-3">
                                <label className="mb-1 block text-xs font-medium text-gray-600">Notes</label>
                                <textarea
                                    value={draft.notes ?? ''}
                                    onChange={(e) => setDraft(d => ({ ...d, notes: e.target.value }))}
                                    rows={3}
                                    aria-label="Customer notes"
                                    placeholder="Anything worth remembering about this customer"
                                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500"
                                />
                            </div>

                            {/* Stated plainly rather than discovered later: this is not a
                                local edit, and staff should know before they save. */}
                            <p className="mt-2 text-[11px] text-amber-700">
                                Changing the name updates it on this customer's past invoices too.
                            </p>

                            {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

                            <div className="mt-3 flex justify-end gap-2">
                                <button type="button" onClick={() => setEditing(false)}
                                        className="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100">
                                    Cancel
                                </button>
                                <button type="button" onClick={save} disabled={saving}
                                        className="inline-flex items-center gap-1 rounded-lg bg-violet-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50">
                                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                                    Save customer
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            {customer.notes && (
                                <div className="mb-3">
                                    <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">Notes</p>
                                    <p className="whitespace-pre-line text-sm text-gray-700">{customer.notes}</p>
                                </div>
                            )}
                            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
                                Vehicles
                            </p>
                            {vehicles.length === 0 ? (
                                <p className="text-xs text-gray-500">No vehicles recorded.</p>
                            ) : (
                                <ul className="space-y-1.5">
                                    {vehicles.map((v) => (
                                        <VehicleRow key={v.uuid} vehicle={v} onSaved={onSaved}
                                                    canDelete={canDeleteVehicle} />
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </div>
            )}
        </li>
    );
};

// ── Tab ───────────────────────────────────────────────────────────────────────

const CustomersTab = () => {
    const [search, setSearch] = useState('');
    const [applied, setApplied] = useState('');     // '' = browsing by month
    const [month, setMonth] = useState(currentMonthValue());
    const [page, setPage] = useState(0);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /*
     * Read once per MOUNT, via a lazy initialiser rather than a bare call.
     *
     * hasPermission goes to localStorage, base64-decodes the JWT and JSON.parses the
     * payload on every call. A bare `const x = hasPermission(...)` re-runs that on every
     * render of this tab — three times before the first list even paints, and again on
     * every search, page and month change. useState's initialiser is guaranteed to run
     * exactly once, which useMemo is not.
     *
     * Once per mount is the right lifetime: the claim only changes with a new token, and
     * that path remounts the page.
     */
    const [canDeleteVehicle] = useState(() => hasPermission('vehicle:delete'));
    const [canDeleteCustomer] = useState(() => hasPermission('customer:delete'));

    // The customer awaiting confirmation, or null. Held here rather than per row so the
    // dialog is a single instance — one per row would mean N mounted dialogs on a page
    // of 20, each with its own state.
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);


    /*
     * One loader for both modes, because they are the same list with a different bound.
     * A search reaches customers of ANY age; without one, the month window applies. The
     * server enforces the same rule, so an unbounded browse is impossible either way.
     */
    const load = useCallback(async ({ nextPage = 0, term, monthValue } = {}) => {
        const useTerm = term !== undefined ? term : applied;
        const range = rangeFromMonth(monthValue !== undefined ? monthValue : month);

        setLoading(true);
        setError(null);
        try {
            setData(await listCustomersApi({
                fleet: false,
                search: useTerm || undefined,
                createdFrom: useTerm ? undefined : range.from,
                createdTo: useTerm ? undefined : range.to,
                page: nextPage,
                size: PAGE_SIZE,
            }));
            setPage(nextPage);
        } catch (e) {
            setError(e?.response?.data?.message || 'Could not load customers.');
        } finally {
            setLoading(false);
        }
    }, [applied, month]);

    // Opens on this month's new customers — a short, useful list — rather than everything.
    useEffect(() => { load({ nextPage: 0, term: '', monthValue: currentMonthValue() }); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const submitSearch = (e) => {
        e.preventDefault();
        setApplied(search);
        load({ nextPage: 0, term: search });
    };

    const clearSearch = () => {
        setSearch('');
        setApplied('');
        load({ nextPage: 0, term: '' });
    };

    const goMonth = (next) => {
        setMonth(next);
        load({ nextPage: 0, term: '', monthValue: next });
    };

    const askDelete = (customer) => {
        setDeleteError(null);
        setPendingDelete(customer);
    };

    const confirmDelete = async () => {
        setDeleting(true);
        setDeleteError(null);
        try {
            await deleteCustomerApi(pendingDelete.uuid);
            setPendingDelete(null);
            // Reload the page being viewed rather than splicing locally, so the counts
            // and pagination stay in step with the server.
            load({ nextPage: page });
        } catch (e) {
            // The server names how many invoices, jobs or bookings are blocking, or that
            // a portal login is in the way. Keep the dialog OPEN so that message is read
            // next to the name that was just typed.
            setDeleteError(e?.response?.data?.message || 'Could not delete this customer.');
        } finally {
            setDeleting(false);
        }
    };

    const customers = data?.customers ?? [];
    const totalPages = data?.totalPages ?? 0;
    const searching = !!applied;

    return (
        <div>
            <form onSubmit={submitSearch} className="mb-3 flex gap-2">
                <div className="relative flex-1">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name, phone or rego — any date"
                        className="w-full rounded-xl border border-gray-200 py-2.5 pl-10 pr-3 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500"
                    />
                </div>
                <button type="submit"
                        className="rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-violet-700">
                    Search
                </button>
                {searching && (
                    <button type="button" onClick={clearSearch}
                            className="rounded-xl px-3 py-2.5 text-sm text-gray-600 hover:bg-gray-100">
                        Clear
                    </button>
                )}
            </form>

            {/* Month navigation — only meaningful while browsing. A search deliberately
                ignores the window, so showing it then would imply a filter that is not
                being applied. */}
            {!searching ? (
                <div className="mb-4 flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                    <button type="button" onClick={() => goMonth(shiftMonth(month, -1))} disabled={loading}
                            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-gray-600 hover:bg-white disabled:opacity-40">
                        <ChevronLeft size={14} /> Previous
                    </button>
                    <label className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-800">
                        <Calendar size={14} className="text-gray-400" />
                        <span>New customers</span>
                        {/* Editable so a month years back is one action, not twelve clicks
                            on Previous. max stops a browse into the future, which can only
                            ever be empty. */}
                        <input
                            type="month"
                            value={month}
                            max={currentMonthValue()}
                            disabled={loading}
                            onChange={(e) => e.target.value && goMonth(e.target.value)}
                            className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm font-medium text-gray-900 focus:border-violet-500 focus:ring-2 focus:ring-violet-500"
                        />
                    </label>
                    <button type="button" onClick={() => goMonth(shiftMonth(month, 1))}
                            disabled={loading || month >= currentMonthValue()}
                            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-gray-600 hover:bg-white disabled:opacity-40">
                        Next <ChevronRight size={14} />
                    </button>
                </div>
            ) : (
                <p className="mb-4 text-xs text-gray-500">
                    Showing search results across all dates.
                </p>
            )}

            {error && (
                <div className="mb-3 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <AlertCircle size={15} /> {error}
                </div>
            )}

            {loading && customers.length === 0 && (
                <div className="flex justify-center py-12">
                    <Loader2 size={22} className="animate-spin text-violet-600" />
                </div>
            )}

            {!loading && customers.length === 0 && !error && (
                <p className="py-12 text-center text-sm text-gray-500">
                    {searching
                        ? 'No customers match that search.'
                        : `No new customers in ${rangeFromMonth(month).label}. Search above to find an existing one.`}
                </p>
            )}

            <ul className="space-y-2">
                {customers.map((c) => (
                    <CustomerRow key={c.uuid} customer={c} onSaved={() => load({ nextPage: page })}
                                 canDeleteVehicle={canDeleteVehicle}
                                 canDelete={canDeleteCustomer} onDelete={askDelete} />
                ))}
            </ul>

            {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                    <button type="button" disabled={page === 0 || loading}
                            onClick={() => load({ nextPage: page - 1 })}
                            className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40">
                        <ChevronLeft size={14} /> Previous
                    </button>
                    <span className="text-sm text-gray-500">
                        Page {page + 1} of {totalPages} · {data?.totalItems ?? 0} customers
                    </span>
                    <button type="button" disabled={page + 1 >= totalPages || loading}
                            onClick={() => load({ nextPage: page + 1 })}
                            className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40">
                        Next <ChevronRight size={14} />
                    </button>
                </div>
            )}

            <DeleteCustomerDialog
                open={!!pendingDelete}
                customer={pendingDelete}
                onConfirm={confirmDelete}
                onClose={() => setPendingDelete(null)}
                deleting={deleting}
                error={deleteError}
            />
        </div>
    );
};

export default CustomersTab;
